import {
  Button,
  Flex,
  Grid,
  Heading,
  IconArrowOpenDownSolid,
  IconArrowOpenEndSolid,
  IconButton,
  IconInfoSolid,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  Text,
  Tooltip,
  Transition,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Card } from "../../components/Card";
import { CourseLevelSelect } from "../../components/Selectors/CourseLevelSelect";
import VarietyCheckbox from "../../components/Selectors/Variety/VarietyCheckbox";
import AcademicYearSelect from "../../components/TermManagement/AcademicYears/AcademicYearSelect";
import { TermSelect } from "../../components/TermManagement/Terms/TermSelect";
import LineChart from "../../sections/Cards/LineChart";
import OutcomesLink from "../../sections/Cards/OutcomesLink";
import { TermsContext } from "../../utils/Context";

const CourseChart = ({ filters, offering, setFilters }) => {
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const [reloadOutcomeResults, setReloadOutcomeResults] = useState(true);
  const [chartTrigger, setChartTrigger] = useState(false);
  const [trigger, setTrigger] = useState(false);
  const [resultOption, setResultOption] = useState("dept");
  const [selectedAcademicYears, setSelectedAcademicYears] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [selectedTerms, setSelectedTerms] = useState([]);

  const loInputs = [
    { value: "ISLO", label: "ILO" },
    { value: "PSLO", label: "PLO" },
    { value: "CSLO", label: "CLO" },
  ];

  const measuredAt = [
    { value: "true", label: `${t("dashboard.achievedMastery")} %` },
    { value: "false", label: `${t("dashboard.scoreAvg")} %` },
  ];

  const resultsViewOptions = [
    {
      value: "dept",
      label: `${t("results.dept")} ${offering.academic_subject.title} ${t(
        "results.results"
      )}`,
      typeFilter: "dept",
    },
    {
      value: offering.parent_course_id,
      label: `${t("results.course")} ${offering.code} ${t("results.results")}`,
      typeFilter: "course",
    },
    {
      value: offering.id,
      label: `${t("results.offering")}: ${offering.code} ${t(
        "results.results"
      )}`,
      typeFilter: "offerings",
    },
  ];

  useEffect(() => {
    if (canvasTerms.length > 0) {
      const termList = [];
      filters.terms.forEach((t) => {
        const canvasTerm = canvasTerms.find(
          (canvasTerm) => canvasTerm.canvas_id === t
        );
        if (!termList.includes(canvasTerm.insights_term_id)) {
          termList.push(canvasTerm.insights_term_id);
        }
      });
      setSelectedTerms(termList);
    }
  }, [canvasTerms, filters.terms]);

  const handleLOTypeChange = (event) => {
    if (setFilters) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        LOType: event.target.value,
      }));
    }
    if (event.target.value === "CSLO") {
      setFilters((prevFilters) => ({
        ...prevFilters,
        variety: ["outcome"],
        courses: [offering.parent_course_id],
      }));
      setResultOption(offering.parent_course_id);
    }
  };

  const handleResultsViewChange = (e) => {
    if (setFilters) {
      setResultOption(e.target.value);
      setFilters((prevFilters) => ({
        ...prevFilters,
        courses:
          e.target.value === offering.parent_course_id ? [e.target.value] : [],
        offerings: e.target.value === offering.id ? [e.target.value] : [],
      }));
    }
  };

  const handleResultsTypeChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      masteryResults: event.target.value,
    }));
  };

  const handleAcademicYearSelect = (years) => {
    setSelectedAcademicYears(years);
    // Filter out future insights terms
    let filteredInsightsTerms = years.flatMap((year) => year.insights_terms);
    filteredInsightsTerms = filteredInsightsTerms.filter(
      (term) =>
        new Date(term.start_at).setHours(0, 0, 0, 0) <=
        new Date().setHours(0, 0, 0, 0)
    );
    handleSelectedTerms(filteredInsightsTerms.map((t) => t.id));
  };

  const handleSelectedTerms = (terms) => {
    const selectedInsightsTerms = [];
    terms.forEach((term) => {
      selectedInsightsTerms.push(insightsTerms.find((t) => t.id === term));
    });

    const canvasTermIdList = [];
    selectedInsightsTerms.forEach((term) => {
      canvasTermIdList.push(
        ...term.canvas_terms
          .filter((canvasTerm) => canvasTerm.canvas_id !== -1)
          .map((canvasTerm) => canvasTerm.canvas_id)
      );
    });

    setFilters((prevFilters) => ({ ...prevFilters, terms: canvasTermIdList }));
  };

  const handleSelectedCourseLevels = (levels) => {
    setFilters((prevFilters) => ({ ...prevFilters, courseLevels: levels }));
  };

  return (
    <Card style={{ textAlign: "center" }}>
      <Flex wrap="wrap" gap="small">
        <Flex.Item>
          <IconButton
            withBackground={false}
            withBorder={false}
            renderIcon={
              expanded ? IconArrowOpenDownSolid : IconArrowOpenEndSolid
            }
            screenReaderLabel={
              expanded ? t("dashboard.loResults") : t("dashboard.showLoResults")
            }
            size="small"
            onClick={() => setExpanded((prevExpanded) => !prevExpanded)}
          />
        </Flex.Item>
        <Flex.Item>
          <Heading as="h2" level="h4">
            {expanded ? t("dashboard.loResults") : t("dashboard.showLoResults")}
          </Heading>
        </Flex.Item>
        <Flex.Item shouldGrow>
          <OutcomesLink
            reload={reloadOutcomeResults}
            setReload={setReloadOutcomeResults}
            subject={filters.subject}
            searchTrigger={trigger || chartTrigger}
          />
        </Flex.Item>
      </Flex>
      {expanded && (
        <Transition transitionOnMount in={expanded}>
          <Flex wrap="wrap" gap="small">
            <Flex.Item size="100%">
              <div
                style={{
                  marginTop: "5px",
                  paddingTop: "5px",
                  borderTop: "1px solid #ccc",
                  textAlign: "left",
                }}
              >
                <Text>{t("dashboard.disclaimer")} </Text>
                <Tooltip
                  renderTip={
                    <div style={{ width: "600px" }}>
                      {t("dashboard.chartDataDisclaimer")}
                    </div>
                  }
                  placement="top"
                  on={["click", "hover", "focus"]}
                  color="primary-inverse"
                >
                  <IconInfoSolid color="brand" />
                </Tooltip>
              </div>
            </Flex.Item>
            <LineChart
              size="100%"
              filters={filters}
              trigger={trigger || chartTrigger}
              setTrigger={setTrigger}
              setChartTrigger={setChartTrigger}
            />
            <Flex.Item size="100%" padding="0 small">
              <Grid startAt="large">
                <Grid.Row hAlign="space-around">
                  <Grid.Col width="auto">
                    <div style={{ marginBottom: 10 }}>
                      <RadioInputGroup
                        layout="columns"
                        name="loType"
                        defaultValue="PSLO"
                        description={
                          <Text size="small">{t("filter.loType")}</Text>
                        }
                        size="small"
                        onChange={handleLOTypeChange}
                      >
                        {loInputs.map((input) => (
                          <RadioInput
                            key={input.value}
                            value={input.value}
                            label={input.label}
                          />
                        ))}
                      </RadioInputGroup>
                      <div style={{ marginTop: 10 }}>
                        <VarietyCheckbox
                          filters={filters}
                          selectHandler={(v) =>
                            setFilters((prevFilters) => ({
                              ...prevFilters,
                              variety: v,
                            }))
                          }
                        />
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <RadioInputGroup
                          layout="stacked"
                          name={t("results.resultsBy")}
                          value={resultOption}
                          description={
                            <ScreenReaderContent>
                              {t("results.view")}
                            </ScreenReaderContent>
                          }
                          size="small"
                          onChange={handleResultsViewChange}
                        >
                          {resultsViewOptions.map((option) => (
                            <RadioInput
                              key={option.value}
                              value={option.value}
                              label={option.label}
                              disabled={
                                option.value === "dept" &&
                                filters.LOType === "CSLO"
                              }
                            />
                          ))}
                        </RadioInputGroup>
                      </div>
                    </div>
                  </Grid.Col>
                  <Grid.Col>
                    <RadioInputGroup
                      layout="stacked"
                      name="measured"
                      defaultValue={
                        filters.masteryResults ? filters.masteryResults : "true"
                      }
                      description={
                        <Text size="small">{t("filter.masteryResults")}</Text>
                      }
                      size="small"
                      onChange={handleResultsTypeChange}
                    >
                      {measuredAt.map((input) => (
                        <RadioInput
                          key={input.value}
                          value={input.value}
                          label={input.label}
                        />
                      ))}
                    </RadioInputGroup>
                  </Grid.Col>
                  <Grid.Col>
                    <CourseLevelSelect
                      id="lo-filter-course-level-select"
                      selectHandler={handleSelectedCourseLevels}
                      size="small"
                      selectedLevels={filters.courseLevels ?? []}
                    />
                  </Grid.Col>
                  <Grid.Col>
                    <AcademicYearSelect
                      selectHandler={handleAcademicYearSelect}
                      noFutureYears
                      selectedAcademicYears={selectedAcademicYears.map(
                        (year) => year.id
                      )}
                      size="small"
                    />
                    <div style={{ marginTop: 10 }}>
                      <TermSelect
                        selectHandler={handleSelectedTerms}
                        selectedAcademicYears={selectedAcademicYears}
                        size="small"
                        selectedTerms={selectedTerms}
                        noFutureTerms={true}
                        label={t("terms")}
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col vAlign="bottom" width="auto">
                    <Button
                      display="block"
                      size="medium"
                      id="apply-filters-button"
                      color="primary"
                      onClick={() => setChartTrigger(true)}
                    >
                      {t("filter.applyButton")}
                    </Button>
                  </Grid.Col>
                </Grid.Row>
              </Grid>
            </Flex.Item>
          </Flex>
        </Transition>
      )}
    </Card>
  );
};

export default CourseChart;
